import { Injectable } from '@angular/core';

import { merge, Observable, tap } from 'rxjs';

import { Schedule } from '@shared/schedule/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { ApiClientService } from '@tablesready/api-client';
import { ScheduleQuery, ScheduleStore, SocketService } from '@tablesready/frontend-store';

@Injectable({providedIn: 'root'})
export class ScheduleService {
  private readonly basePath = '/v.1/schedule';

  constructor(
    private readonly store: ScheduleStore,
    private readonly query: ScheduleQuery,
    private readonly http: ApiClientService,
    private readonly socketService: SocketService,
  ) {
  }

  load() {
    return this.http.get<Schedule[]>(this.basePath)
      .pipe(tap(schedules => this.store.set(schedules)));
  }

  selectAll(): Observable<Schedule[]> {
    return this.query.selectAll();
  }

  create(data: Omit<Schedule, '_id'>): Observable<void> {
    return this.http.post<void>(`${this.basePath}`, data);
  }

  updateById(id: string, data: Omit<Schedule, '_id'>): Observable<void> {
    return this.http.put<void>(`${this.basePath}/${id}`, data);
  }

  deleteById(id: string): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/${id}`);
  }

  listenToChanges() {
    return merge(
      this.socketService.getEventStream<Schedule[]>(WebSocketEventsEnum.scheduleCreated)
        .pipe(tap(schedules => this.store.add(schedules))),
      this.socketService.getEventStream<Schedule[]>(WebSocketEventsEnum.scheduleUpdated)
        .pipe(tap(schedules => {
          for (let i = 0; i < schedules.length; i++) {
            this.store.update(schedules[i]._id, schedules[i]);
          }
        })),
      this.socketService.getEventStream<string[]>(WebSocketEventsEnum.scheduleDeleted)
        .pipe(tap(ids => this.store.remove(ids))),
    );
  }

  reset() {
    this.store.reset();
  }
}
